import { Check, ChevronsUpDown, X } from 'lucide-react'
import { useId, useMemo, useRef, useState } from 'react'
import { Button, Input, ListBox, ListBoxItem, Popover, DialogTrigger } from 'react-aria-components'
import { cn } from '../lib/cn'
import { filterHierarchicalOptions, type ComboboxOption } from './combobox'

export interface MultiComboboxProps {
	options: ComboboxOption[]
	/** Selected option values (controlled). */
	value: string[]
	/** Fires with the full next selection, never a single toggle. */
	onChange: (values: string[]) => void
	placeholder?: string
	isDisabled?: boolean
	isInvalid?: boolean
	isRequired?: boolean
	className?: string
	/** Accessible name — REQUIRED (there is no visible label inside this atom). */
	'aria-label'?: string
	'aria-labelledby'?: string
}

/**
 * Searchable MULTI-select combobox — the multi counterpart of {@link Combobox}.
 *
 * Exists because neither sibling covered this: `Combobox` is single-select, and
 * `AxisMultiSelect` takes a `FilterAxis` rather than generic options, so it is the
 * filter rail's control and cannot serve a form. Anything needing several values
 * from a long list therefore fell back to a native `<select multiple>` — which has
 * no type-ahead, is unusable past a couple of dozen options, and looks nothing like
 * the rest of the design system.
 *
 * Built on `DialogTrigger` + `Popover` rather than React Aria's `ComboBox`, because
 * `ComboBox` is single-select by contract: its input text IS the selection, so it
 * closes and overwrites on every pick. Here the input is a SEARCH box that stays put
 * while the list is toggled, which is the behaviour a multi-select needs.
 *
 * Filters an in-memory list, like `Combobox` — no server search.
 */
export function MultiCombobox({
	options,
	value,
	onChange,
	placeholder = 'Select…',
	isDisabled,
	isInvalid,
	isRequired,
	className,
	'aria-label': ariaLabel,
	'aria-labelledby': ariaLabelledBy,
}: MultiComboboxProps) {
	const [query, setQuery] = useState('')
	const searchRef = useRef<HTMLInputElement>(null)
	const listRef = useRef<HTMLDivElement>(null)
	const invalidHintId = useId()

	// The sibling's filter, not a plain substring match: this control renders `depth`
	// and ships a hierarchical story, and a flat "contains" hides a matching child
	// whose parent does not match while stripping the ancestors that give a deep match
	// its meaning. Reused rather than reimplemented so the two controls agree.
	const filtered = useMemo(() => filterHierarchicalOptions(options, query), [options, query])

	const selectedLabels = options.filter((o) => value.includes(o.value)).map((o) => o.label)

	return (
		<DialogTrigger onOpenChange={(open) => open && setQuery('')}>
			{isInvalid ? (
				<span id={invalidHintId} className="sr-only">
					Invalid selection
				</span>
			) : null}
			<Button
				aria-label={ariaLabel}
				aria-labelledby={ariaLabelledBy}
				aria-required={isRequired}
				// Invalidity has to be ANNOUNCED, not only coloured: `isInvalid` used to change
				// the border and nothing else, so a screen-reader user got no signal — the gap
				// `Combobox` already had fixed for the single-select variant.
				//
				// It travels as a description rather than as `aria-invalid`, because React
				// Aria's `Button` filters that attribute out (it forwards the labelling aria-*
				// props and drops this one — verified against the rendered markup). A button is
				// not a form control in the ARIA sense anyway, so a described state is the
				// honest encoding: the trigger keeps `data-invalid` for styling, and the
				// description is what a reader hears.
				data-invalid={isInvalid || undefined}
				aria-describedby={isInvalid ? invalidHintId : undefined}
				isDisabled={isDisabled}
				className={cn(
					'flex h-10 w-full items-center justify-between gap-2 rounded-ds-md border bg-ds-background pl-3 pr-2 text-left text-ds-base text-ds-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:cursor-not-allowed disabled:opacity-50',
					isInvalid ? 'border-ds-danger focus-visible:ring-ds-danger' : 'border-ds-border',
					className,
				)}
			>
				{/*
					The trigger summarises rather than listing chips: a summary keeps the
					control one row tall whatever is chosen, so a form does not reflow as
					someone selects.
				*/}
				<span className={cn('truncate', selectedLabels.length === 0 && 'text-ds-text-subtle')}>
					{selectedLabels.length === 0 ? placeholder : selectedLabels.join(', ')}
				</span>
				<span className="flex shrink-0 items-center gap-1">
					{selectedLabels.length > 0 ? (
						<span className="rounded-ds-sm bg-ds-surface px-1.5 text-ds-2xs text-ds-text-muted">
							{selectedLabels.length}
						</span>
					) : null}
					<ChevronsUpDown aria-hidden className="size-4 text-ds-text-subtle" />
				</span>
			</Button>

			<Popover className="w-(--trigger-width) rounded-ds-md border border-ds-border bg-ds-background shadow-ds-md">
				<div className="p-2">
					<div className="relative">
						<Input
							ref={searchRef}
							// `autoFocus` is right here and only here: the popover exists to be
							// typed into, and a multi-select's whole value is finding an option
							// without scrolling for it.
							autoFocus
							aria-label="Filter options"
							value={query}
							placeholder="Search…"
							onChange={(e) => setQuery(e.target.value)}
							// Arrow keys move from the search box INTO the list. Without this the
							// only route to an option was Tab, past the clear button — the search
							// autofocuses, so the first keystroke a keyboard user makes after
							// typing had nowhere to go. React Aria's ListBox takes focus and its
							// own roving-tabindex handling from here.
							onKeyDown={(e) => {
								if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
								const first = listRef.current?.querySelector<HTMLElement>('[role="option"]')
								if (!first) return
								e.preventDefault()
								first.focus()
							}}
							className="h-8 w-full rounded-ds-md border border-ds-border bg-ds-background pl-2 pr-7 text-ds-sm text-ds-text placeholder:text-ds-text-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring"
						/>
						{query !== '' ? (
							<Button
								aria-label="Clear search"
								onPress={() => {
									setQuery('')
									searchRef.current?.focus()
								}}
								className="absolute right-1 top-1 rounded-ds-sm p-1 text-ds-text-subtle hover:text-ds-text"
							>
								<X aria-hidden className="size-3.5" />
							</Button>
						) : null}
					</div>
				</div>

				{filtered.length === 0 ? (
					<p className="px-3 pb-3 text-ds-sm text-ds-text-muted">No matches.</p>
				) : (
					<ListBox
						ref={listRef}
						// Falls back to the literal only when the caller named the field NEITHER
						// way: forwarding just `aria-label` announced "Options" to anyone who
						// had labelled the control with `aria-labelledby`.
						aria-label={ariaLabelledBy ? undefined : (ariaLabel ?? 'Options')}
						aria-labelledby={ariaLabelledBy}
						selectionMode="multiple"
						// Selection is React Aria's own, with the FULL next set derived from the
						// keys it reports. Driving it from `onAction` instead looked simpler and
						// was wrong: with `selectedKeys` also set, React Aria stops firing
						// `onAction` for a row it already considers selected, so the second and
						// subsequent picks silently did nothing.
						selectedKeys={value}
						// `'all'` is React Aria's select-all sentinel, and it must resolve to what
						// is RENDERED. Mapping it over every option meant Ctrl/Cmd+A after typing
						// a search also selected the rows the search had hidden.
						onSelectionChange={(keys) =>
							onChange(keys === 'all' ? filtered.map((o) => o.value) : [...keys].map(String))
						}
						className="max-h-60 overflow-auto p-1 outline-none"
					>
						{filtered.map((option) => (
							<ListBoxItem
								key={option.value}
								id={option.value}
								textValue={option.label}
								className="flex cursor-pointer items-center justify-between gap-2 rounded-ds-sm px-2 py-1.5 text-ds-sm text-ds-text outline-none data-[focused]:bg-ds-surface"
								style={option.depth ? { paddingLeft: `${0.5 + option.depth}rem` } : undefined}
							>
								<span className="min-w-0">
									<span className="block truncate">{option.label}</span>
									{option.description === undefined ? null : (
										<span className="block truncate text-ds-2xs text-ds-text-subtle">{option.description}</span>
									)}
								</span>
								{value.includes(option.value) ? <Check aria-hidden className="size-4 shrink-0" /> : null}
							</ListBoxItem>
						))}
					</ListBox>
				)}

				{value.length > 0 ? (
					<div className="border-t border-ds-border p-2">
						<Button
							onPress={() => onChange([])}
							className="rounded-ds-sm px-2 py-1 text-ds-2xs text-ds-text-muted hover:text-ds-text"
						>
							Clear all
						</Button>
					</div>
				) : null}
			</Popover>
		</DialogTrigger>
	)
}
